// ============================================================================
// Motor de temas del storefront (Parte 2).
//
// Objetivo: EDITOR = PREVIEW = TIENDA PÚBLICA. El storefront debe pintar EXACTA
// la configuracion establecida en Panel Cliente -> Personalizar tienda:
//   - `tema`   (tema_visual): colores, tipografia, sombras, espaciado, radio.
//   - `editor` (editor_tienda): variante de cabecera, estilo/radio/ancho de
//     botones, estilo/radio/sombra de tarjetas y `seccion_orden` del home.
//
// `publicar_tienda` ya emite `empresa.editor` (Parte 2). Cuando la tienda aun
// no uso el editor (o la publicacion no lo incluye), este modulo degrada a los
// campos del constructor de `tema_visual` (cabecera, boton_estilo,
// tarjeta_estilo, ...) para que ninguna tienda publica pierda personalizacion.
//
// IMPORTANTE (coherencia con "un solo motor"): los catalogos de aqui son el
// ESPEJO de los del Panel Cliente (apps/panel-cliente/src/js/editor/temas.js y
// apps/panel-cliente/src/js/api/editor-tienda.js), que son la FUENTE DE VERDAD
// viva. El antigo motor-tema del panel usaba otros ids (solido/contorno/...,
// clara/sombra/...); se reconcilió en la Parte 2 para que todo hable el mismo
// idioma: redondeado/cuadrado/pill/outline y borde/relleno/elevado.
// ============================================================================

// ---------------------------------------------------------------------------
// Variantes de cabecera (espejo de editor/temas.js#HEADER_VARIANTES)
// ---------------------------------------------------------------------------
export const HEADER_VARIANTES = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion:
      'Universal y equilibrado: logo centrado, navegación horizontal, acciones a la derecha.',
    minimo: 'Prueba',
    caracteristicas: {
      alineacion: 'centro',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: true,
      estructura: 'centrado',
      busqueda: false,
      barraCategorias: false,
      navConIconos: true,
    },
  },
  {
    id: 'ecommerce',
    nombre: 'Ecommerce',
    descripcion: 'Orientado a venta: búsqueda accesible, categorías visibles, carrito prominente.',
    minimo: 'Medio',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'categorias',
      fondo_solido: true,
      buscador: true,
      whatsapp: true,
      estructura: 'busqueda-lateral',
      busqueda: true,
      barraCategorias: true,
      navConIconos: false,
      busquedaExpandible: true,
    },
  },
  {
    id: 'premium',
    nombre: 'Premium',
    descripcion:
      'Sofisticado: logo grande, navegación integrada, transparente sobre hero, scroll-aware.',
    minimo: 'Medio',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: false,
      whatsapp: true,
      estructura: 'hero-integrado',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      transparente: true,
      logoGrande: true,
      scrollAware: true,
      accionesDiscretas: true,
    },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion:
      'Essencial: máximo espacio, solo lo necesario, logo protagonista, acciones por iconos.',
    minimo: 'Medio',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'minimal',
      fondo_solido: false,
      whatsapp: false,
      estructura: 'minimal',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      soloEsenciales: true,
      muchoEspacio: true,
    },
  },
  {
    id: 'moderno',
    nombre: 'Moderno',
    descripcion:
      'Audaz y contemporáneo: franja útil arriba, marca en negrita, navegación con iconos y acciones claras.',
    minimo: 'Medio',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: false,
      estructura: 'doble-plano',
      busqueda: false,
      barraCategorias: false,
      navConIconos: true,
      franjaUtil: true,
      bold: true,
    },
  },
  {
    id: 'marketplace',
    nombre: 'Marketplace',
    descripcion:
      'Gran mercado: franja oscura, búsqueda amplia y barra gruesa de categorías para escanear y comprar.',
    minimo: 'Medio',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'categorias',
      fondo_solido: true,
      buscador: true,
      whatsapp: false,
      estructura: 'multi-capa',
      busqueda: true,
      barraCategorias: true,
      navConIconos: false,
      franjaOscura: true,
      busquedaAmplia: true,
    },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion:
      'Estilo revista: cabecera alta, navegación versalitas sobre segunda línea, hairlines finas.',
    minimo: 'Pro',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: true,
      whatsapp: false,
      estructura: 'revista',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      alto: true,
      versales: true,
    },
  },
  {
    id: 'flotante',
    nombre: 'Flotante',
    descripcion:
      'Cristal: transparente sobre el hero con el contenido en una pastilla flotante con sombra y blur.',
    minimo: 'Pro',
    caracteristicas: {
      alineacion: 'izquierda',
      menu: 'lineal',
      fondo_solido: false,
      whatsapp: false,
      estructura: 'pastilla',
      busqueda: false,
      barraCategorias: false,
      navConIconos: false,
      transparente: true,
      flotante: true,
      scrollAware: true,
    },
  },
];

// ---------------------------------------------------------------------------
// Variantes de categorías destacadas (espejo de editor/temas.js#VARIANTES_CATEGORIAS)
// ---------------------------------------------------------------------------
// Cada variante es una COMPOSICIÓN visual distinta de las tarjetas de
// categoría (no un simple cambio de columnas). El markup de la tarjeta es
// siempre el mismo (ver `home.js#bloqueCategorias`); lo que cambia es el
// contenedor `sp-categorias--{id}` y los modificadores
// `sp-categoria--protagonista`/`sp-categoria--secundaria` que engancha el
// CSS para componer cada layout.
export const VARIANTES_CATEGORIAS = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion: 'Grid equilibrado de tarjetas uniformes.',
    minimo: 'Prueba',
    caracteristicas: { protagonista: false, compacto: false, horizontal: false, jerarquia: false },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion: 'Una categoría protagonista grande y otras secundarias.',
    minimo: 'Medio',
    caracteristicas: { protagonista: true, compacto: false, horizontal: false, jerarquia: true },
  },
  {
    id: 'mosaico',
    nombre: 'Mosaico',
    descripcion: 'Composición tipo masonry controlado, con alturas firmes y sin mosaico aleatorio.',
    minimo: 'Medio',
    caracteristicas: { protagonista: false, compacto: false, horizontal: false, jerarquia: false },
  },
  {
    id: 'panorama',
    nombre: 'Panorama',
    descripcion: 'Una categoría horizontal protagonista y el resto en grid debajo.',
    minimo: 'Medio',
    caracteristicas: {
      protagonista: true,
      panorama: true,
      compacto: false,
      horizontal: false,
      jerarquia: true,
    },
  },
  {
    id: 'compacto',
    nombre: 'Compacto',
    descripcion: 'Más densidad visual con tarjetas uniformes y limpias.',
    minimo: 'Medio',
    caracteristicas: { protagonista: false, compacto: true, horizontal: false, jerarquia: false },
  },
  {
    id: 'horizontal',
    nombre: 'Horizontal',
    descripcion: 'Tarjetas panorámicas con desplazamiento horizontal elegante.',
    minimo: 'Medio',
    caracteristicas: { protagonista: false, compacto: false, horizontal: true, jerarquia: false },
  },
  {
    id: 'magazine',
    nombre: 'Magazine',
    descripcion: 'Composición editorial moderna con jerarquía visual marcada.',
    minimo: 'Pro',
    caracteristicas: {
      protagonista: true,
      grande: true,
      compacto: false,
      horizontal: false,
      jerarquia: true,
    },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Composición limpia, mucho aire, imágenes protagonistas y texto elegante.',
    minimo: 'Pro',
    caracteristicas: {
      protagonista: false,
      minimal: true,
      compacto: false,
      horizontal: false,
      jerarquia: false,
    },
  },
];

// ---------------------------------------------------------------------------
// Estilos de botones y tarjetas (espejo de editor/temas.js)
// ---------------------------------------------------------------------------
export const ESTILOS_BOTONES = ['redondeado', 'cuadrado', 'pill', 'outline'];
export const ESTILOS_TARJETAS = ['borde', 'relleno', 'elevado'];

// ---------------------------------------------------------------------------
// Secciones del home (espejo de api/editor-tienda.js#SECCIONES_CONOCIDAS)
// ---------------------------------------------------------------------------
export const SECCIONES_CONOCIDAS = [
  'banner_principal',
  'categorias',
  'propuesta_valor',
  'productos_destacados',
  'productos_nuevos',
  'promociones',
  'novedades',
  'testimonios',
  'footer',
];

export const SECCIONES_PRODUCTOS = new Set([
  'productos_destacados',
  'productos_nuevos',
  'promociones',
  'novedades',
]);

// ---------------------------------------------------------------------------
// Variantes de presentación de las secciones (espejo de editor/temas.js)
// ---------------------------------------------------------------------------
export const VARIANTES_PROPUESTA = [
  {
    id: 'banda',
    nombre: 'Banda',
    descripcion: 'Los puntos en una franja horizontal continua.',
    caracteristicas: { tarjetas: false, principal: false, minimal: false },
  },
  {
    id: 'tarjetas',
    nombre: 'Tarjetas',
    descripcion: 'Cada punto en su propia tarjeta con icono.',
    caracteristicas: { tarjetas: true, principal: false, minimal: false },
  },
  {
    id: 'editorial',
    nombre: 'Editorial',
    descripcion: 'Titular destacado y puntos en dos columnas.',
    caracteristicas: { tarjetas: false, principal: true, minimal: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Línea discreta y centrada, solo lo esencial.',
    caracteristicas: { tarjetas: false, principal: false, minimal: true },
  },
];

export const VARIANTES_PRODUCTOS = [
  {
    id: 'clasico',
    nombre: 'Grid clásico',
    descripcion: 'Rejilla equilibrada de tarjetas uniformes.',
    caracteristicas: { lista: false, compacto: false, destacado: false },
  },
  {
    id: 'lista',
    nombre: 'Lista',
    descripcion: 'Filas horizontales con imagen a la izquierda.',
    caracteristicas: { lista: true, compacto: false, destacado: false },
  },
  {
    id: 'compacto',
    nombre: 'Compacto',
    descripcion: 'Más densidad y columnas.',
    caracteristicas: { lista: false, compacto: true, destacado: false },
  },
  {
    id: 'destacado',
    nombre: 'Destacado',
    descripcion: 'Un producto protagonista grande y el resto en rejilla.',
    caracteristicas: { lista: false, compacto: false, destacado: true },
  },
];

export const VARIANTES_TESTIMONIOS = [
  {
    id: 'tarjetas',
    nombre: 'Tarjetas',
    descripcion: 'Tarjetas uniformes en rejilla.',
    caracteristicas: { central: false, minimal: false, carrusel: false },
  },
  {
    id: 'central',
    nombre: 'Central',
    descripcion: 'Uno protagonista en el centro, el resto debajo.',
    caracteristicas: { central: true, minimal: false, carrusel: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Filas limpias con cita y autor.',
    caracteristicas: { central: false, minimal: true, carrusel: false },
  },
  {
    id: 'carrusel',
    nombre: 'Carrusel',
    descripcion: 'Desplazamiento horizontal elegante.',
    caracteristicas: { central: false, minimal: false, carrusel: true },
  },
];

export const VARIANTES_CONTACTO = [
  {
    id: 'resumen',
    nombre: 'Resumen',
    descripcion: 'Texto a la izquierda y botón a la derecha.',
    caracteristicas: { centrado: false, tarjeta: false },
  },
  {
    id: 'centrado',
    nombre: 'Centrado',
    descripcion: 'CTA centrado y equilibrado.',
    caracteristicas: { centrado: true, tarjeta: false },
  },
  {
    id: 'tarjeta',
    nombre: 'Tarjeta',
    descripcion: 'Bloque en tarjeta con botón prominente.',
    caracteristicas: { centrado: false, tarjeta: true },
  },
];

export const VARIANTES_FOOTER = [
  {
    id: 'clasico',
    nombre: 'Clásico',
    descripcion: 'Marca + columnas de contacto y redes.',
    caracteristicas: { centrado: false, minimal: false },
  },
  {
    id: 'centrado',
    nombre: 'Centrado',
    descripcion: 'Marca y columnas centradas.',
    caracteristicas: { centrado: true, minimal: false },
  },
  {
    id: 'minimal',
    nombre: 'Minimal',
    descripcion: 'Solo marca centrada y legal.',
    caracteristicas: { centrado: true, minimal: true },
  },
];

const VARIANTES_PRODUCTOS_CATALOGO = {
  clasico: 'clasico',
  lista: 'lista',
  compacto: 'compacto',
  destacado: 'destacado',
};
const VARIANTES_PRODUCTOS_VALIDAS = new Set(Object.keys(VARIANTES_PRODUCTOS_CATALOGO));
const VARIANTES_PROPUESTA_VALIDAS = new Set(VARIANTES_PROPUESTA.map((v) => v.id));
const VARIANTES_TESTIMONIOS_VALIDAS = new Set(VARIANTES_TESTIMONIOS.map((v) => v.id));
const VARIANTES_CONTACTO_VALIDAS = new Set(VARIANTES_CONTACTO.map((v) => v.id));
const VARIANTES_FOOTER_VALIDAS = new Set(VARIANTES_FOOTER.map((v) => v.id));
const BANNER_ALINEACIONES_VALIDAS = new Set(['left', 'center', 'right']);

// ---------------------------------------------------------------------------
// Config por defecto del editor (espejo de EDITOR_TIENDA_POR_DEFECTO)
// ---------------------------------------------------------------------------
export const EDITOR_TIENDA_POR_DEFECTO = {
  header: 'clasico',
  categorias_variante: 'clasico',
  botones: { estilo: 'redondeado', radio: 8, ancho_completo: false },
  tarjetas: { estilo: 'borde', radio: 10, sombra: 'suave' },
  seccion_orden: [
    'banner_principal',
    'categorias',
    'propuesta_valor',
    'productos_destacados',
    'footer',
  ],
  tema_preset: 'moderno',
  banner: {
    variante: 'hero',
    overlay: 54,
    alineacion: 'left',
    autoplay: true,
    etiqueta: '',
    texto_boton: 'Ver catálogo',
  },
  propuesta_valor: { variante: 'banda', titulo: '' },
  productos: { variante: 'clasico', titulo: '', eyebrow: '', mostrar_ver_todo: true },
  testimonios: { variante: 'tarjetas', titulo: 'Testimonios' },
  contacto: { variante: 'resumen', titulo: '¿Tienes una duda antes de comprar?', texto: '' },
  footer: { variante: 'clasico' },
};

const HEADERS_VALIDOS = new Set(HEADER_VARIANTES.map((v) => v.id));
const VARIANTES_CATEGORIAS_VALIDAS = new Set(VARIANTES_CATEGORIAS.map((v) => v.id));
const ESTILOS_BOTONES_VALIDOS = new Set(ESTILOS_BOTONES);
const ESTILOS_TARJETAS_VALIDOS = new Set(ESTILOS_TARJETAS);
const SOMBRAS_TARJETAS_VALIDAS = new Set(['ninguna', 'suave', 'media']);

// Mapa de compat: si la tienda configuro el tema con el vocabulario antiguo
// (antes de la Parte 2), sus ids se traducen al idioma del editor.
const MAPA_BOTONES_ANTIGUOS = {
  solido: 'redondeado',
  contorno: 'outline',
  redondeado: 'pill',
  limpio: 'redondeado',
};
const MAPA_TARJETAS_ANTIGUAS = {
  clara: 'borde',
  sombra: 'elevado',
  contorno: 'borde',
  minima: 'borde',
};

function clamp(valor, min, max) {
  return Math.min(max, Math.max(min, valor));
}

/**
 * Normaliza la config del editor a su forma completa y segura.
 * Espejo de `normalizarEditorTienda` del Panel Cliente.
 * @param {object} [config]
 */
export function normalizarEditorTienda(config) {
  const base = EDITOR_TIENDA_POR_DEFECTO;
  const entrada = config ?? {};

  const botones = { ...base.botones, ...(entrada.botones ?? {}) };
  const estiloBoton = ESTILOS_BOTONES_VALIDOS.has(botones.estilo)
    ? botones.estilo
    : (MAPA_BOTONES_ANTIGUOS[botones.estilo] ?? base.botones.estilo);
  botones.estilo = ESTILOS_BOTONES_VALIDOS.has(estiloBoton) ? estiloBoton : base.botones.estilo;
  botones.radio = clamp(Number(botones.radio) || base.botones.radio, 0, 32);
  botones.ancho_completo = Boolean(botones.ancho_completo);

  const tarjetas = { ...base.tarjetas, ...(entrada.tarjetas ?? {}) };
  const estilotarjeta = ESTILOS_TARJETAS_VALIDOS.has(tarjetas.estilo)
    ? tarjetas.estilo
    : (MAPA_TARJETAS_ANTIGUAS[tarjetas.estilo] ?? base.tarjetas.estilo);
  tarjetas.estilo = ESTILOS_TARJETAS_VALIDOS.has(estilotarjeta)
    ? estilotarjeta
    : base.tarjetas.estilo;
  tarjetas.radio = clamp(Number(tarjetas.radio) || base.tarjetas.radio, 0, 32);
  if (!SOMBRAS_TARJETAS_VALIDAS.has(tarjetas.sombra)) tarjetas.sombra = base.tarjetas.sombra;

  const banner = { ...base.banner, ...(entrada.banner ?? {}) };
  if (!BANNER_ALINEACIONES_VALIDAS.has(banner.alineacion))
    banner.alineacion = base.banner.alineacion;
  if (banner.overlay === undefined || banner.overlay === null) banner.overlay = base.banner.overlay;
  banner.overlay = clamp(Number(banner.overlay) || base.banner.overlay, 10, 78);
  banner.autoplay = banner.autoplay !== false;
  banner.texto_boton = String(banner.texto_boton ?? '').trim() || base.banner.texto_boton;
  banner.etiqueta = String(banner.etiqueta ?? '');

  const propuesta_valor = { ...base.propuesta_valor, ...(entrada.propuesta_valor ?? {}) };
  propuesta_valor.variante = VARIANTES_PROPUESTA_VALIDAS.has(propuesta_valor.variante)
    ? propuesta_valor.variante
    : base.propuesta_valor.variante;
  propuesta_valor.titulo = String(propuesta_valor.titulo ?? '');

  const productos = { ...base.productos, ...(entrada.productos ?? {}) };
  productos.variante = VARIANTES_PRODUCTOS_VALIDAS.has(productos.variante)
    ? productos.variante
    : base.productos.variante;
  productos.titulo = String(productos.titulo ?? '');
  productos.eyebrow = String(productos.eyebrow ?? '');
  productos.mostrar_ver_todo = productos.mostrar_ver_todo !== false;

  const testimonios = { ...base.testimonios, ...(entrada.testimonios ?? {}) };
  testimonios.variante = VARIANTES_TESTIMONIOS_VALIDAS.has(testimonios.variante)
    ? testimonios.variante
    : base.testimonios.variante;
  testimonios.titulo = String(testimonios.titulo ?? '');

  const contacto = { ...base.contacto, ...(entrada.contacto ?? {}) };
  contacto.variante = VARIANTES_CONTACTO_VALIDAS.has(contacto.variante)
    ? contacto.variante
    : base.contacto.variante;
  contacto.titulo = String(contacto.titulo ?? '');
  contacto.texto = String(contacto.texto ?? '');

  const footer = { ...base.footer, ...(entrada.footer ?? {}) };
  footer.variante = VARIANTES_FOOTER_VALIDAS.has(footer.variante)
    ? footer.variante
    : base.footer.variante;

  return {
    header: HEADERS_VALIDOS.has(entrada.header) ? entrada.header : base.header,
    categorias_variante: VARIANTES_CATEGORIAS_VALIDAS.has(entrada.categorias_variante)
      ? entrada.categorias_variante
      : base.categorias_variante,
    botones,
    tarjetas,
    seccion_orden: validarSeccionOrden(entrada.seccion_orden, base.seccion_orden),
    tema_preset: typeof entrada.tema_preset === 'string' ? entrada.tema_preset : base.tema_preset,
    banner,
    propuesta_valor,
    productos,
    testimonios,
    contacto,
    footer,
  };
}

/**
 * Resuelve la variante de categorías destacadas del editor a su catálogo
 * completo. Si la tienda no configuró una variante (o la publicación es de
 * una tienda antigua que no la trae) cae a la variante por defecto.
 * @param {object} [editor] - config del editor (normalizada o cruda)
 * @returns {object} entrada de VARIANTES_CATEGORIAS
 */
export function resolverVarianteCategorias(editor) {
  const id = editor?.categorias_variante ?? EDITOR_TIENDA_POR_DEFECTO.categorias_variante;
  return (
    VARIANTES_CATEGORIAS.find((v) => v.id === id) ??
    VARIANTES_CATEGORIAS.find((v) => v.id === EDITOR_TIENDA_POR_DEFECTO.categorias_variante) ??
    VARIANTES_CATEGORIAS[0]
  );
}

function resolverVariante(catalogo, editor, campo, porDefecto) {
  const id = editor?.[campo]?.variante ?? porDefecto.variante;
  return (
    catalogo.find((v) => v.id === id) ??
    catalogo.find((v) => v.id === porDefecto.variante) ??
    catalogo[0]
  );
}

export function resolverVariantePropuesta(editor) {
  return resolverVariante(
    VARIANTES_PROPUESTA,
    editor,
    'propuesta_valor',
    EDITOR_TIENDA_POR_DEFECTO.propuesta_valor,
  );
}

export function resolverVarianteProductos(editor) {
  return resolverVariante(
    VARIANTES_PRODUCTOS,
    editor,
    'productos',
    EDITOR_TIENDA_POR_DEFECTO.productos,
  );
}

export function resolverVarianteTestimonios(editor) {
  return resolverVariante(
    VARIANTES_TESTIMONIOS,
    editor,
    'testimonios',
    EDITOR_TIENDA_POR_DEFECTO.testimonios,
  );
}

export function resolverVarianteContacto(editor) {
  return resolverVariante(
    VARIANTES_CONTACTO,
    editor,
    'contacto',
    EDITOR_TIENDA_POR_DEFECTO.contacto,
  );
}

export function resolverVarianteFooter(editor) {
  return resolverVariante(VARIANTES_FOOTER, editor, 'footer', EDITOR_TIENDA_POR_DEFECTO.footer);
}

function validarSeccionOrden(orden, porDefecto) {
  if (!Array.isArray(orden) || orden.length === 0) return [...porDefecto];
  const conocidas = new Set(SECCIONES_CONOCIDAS);
  const unicos = [...new Set(orden)].filter((codigo) => conocidas.has(codigo));
  if (unicos.length === 0) return [...porDefecto];
  const incluidas = new Set(unicos);
  for (const codigo of porDefecto) {
    if (!incluidas.has(codigo)) unicos.push(codigo);
  }
  return unicos;
}

/**
 * Deriva la config del editor cuando `tienda.editor` viene `null` (tienda que
 * no uso el editor o publicacion moderna pendiente): usa los campos del
 * constructor guardados en `tema_visual`.
 * @param {object} tienda
 */
function editorDesdeTema(tienda) {
  const base = EDITOR_TIENDA_POR_DEFECTO;
  const tema = tienda.tema ?? {};
  const botones = {
    estilo: MAPA_BOTONES_ANTIGUOS[tema.boton_estilo] ?? base.botones.estilo,
    radio: Number(tema.boton_radio) || base.botones.radio,
    ancho_completo: Boolean(tema.boton_ancho_completo),
  };
  const tarjetas = {
    estilo: MAPA_TARJETAS_ANTIGUAS[tema.tarjeta_estilo] ?? base.tarjetas.estilo,
    radio: Number(tema.tarjeta_radio) || base.tarjetas.radio,
    sombra: tema.sombra ?? base.tarjetas.sombra,
  };
  return {
    header: tema.cabecera ?? base.header,
    botones,
    tarjetas,
    tema_preset: base.tema_preset,
    seccion_orden: base.seccion_orden,
  };
}

/**
 * Estado visual completo de la tienda publica, interpretando `tema_visual` +
 * `editor_tienda` y aplicando la misma normalizacion que el Panel Cliente.
 * @param {object} tienda
 */
export function estadoVisual(tienda) {
  const editor = normalizarEditorTienda(tienda.editor ?? editorDesdeTema(tienda));
  const tema = tienda.tema ?? {};
  const planNombre = String(tienda.empresa?.plan_nombre ?? 'Prueba');
  const variante =
    HEADER_VARIANTES.find((v) => v.id === editor.header) ??
    HEADER_VARIANTES.find((v) => v.id === EDITOR_TIENDA_POR_DEFECTO.header) ??
    HEADER_VARIANTES[0];

  return {
    editor,
    header: {
      ...variante,
      fija: tema.cabecera_fija !== false,
      whatsapp: tema.cabecera_whatsapp !== false,
    },
    botones: editor.botones,
    tarjetas: editor.tarjetas,
    seccion_orden: editor.seccion_orden,
    tipografia: ['inter', 'system', 'serif', 'mono', 'rounded'].includes(tema.tipografia)
      ? tema.tipografia
      : 'inter',
    plan: planNombre,
    // branding: se muestra el crédito de la plataforma salvo que el plan sea
    // Pro (feature existente `quitar_branding` — ver matriz de planes).
    branding: planNombre !== 'Pro',
    cerrada: String(tienda.empresa?.estado ?? '') !== 'activa',
  };
}

export function seccionOrden(tienda) {
  return estadoVisual(tienda).seccion_orden;
}

export function tipografiaClase(tipografia) {
  return `sp-tipografia-${tipografia}`;
}

function radioClase(valor) {
  return `radio-${valor}`;
}

export function sombraTarjeta(sombra) {
  const sombras = {
    ninguna: 'none',
    suave: '0 12px 30px rgb(16 38 58 / 0.1)',
    media: '0 18px 45px rgb(16 38 58 / 0.16)',
  };
  return sombras[sombra] ?? 'none';
}

/**
 * Clases de superficie de tarjeta segun el estado visual del editor.
 * @param {object} [estado]
 */
export function clasesTarjetas(estado) {
  const tarjetas = estado?.tarjetas ?? EDITOR_TIENDA_POR_DEFECTO.tarjetas;
  return `sp-tarjeta sp-tarjeta--${tarjetas.estilo} ${radioClase(tarjetas.radio)} sombra-${tarjetas.sombra}`;
}

/**
 * Clases de boton segun el estado visual del editor.
 * @param {object} [estado]
 * @param {string} [adicional] - por ejemplo `sp-boton--primario`
 */
export function clasesBotones(estado, adicional = '') {
  const botones = estado?.botones ?? EDITOR_TIENDA_POR_DEFECTO.botones;
  const modulos = [
    'sp-boton',
    `sp-boton--${botones.estilo}`,
    adicional,
    botones.ancho_completo ? 'sp-boton--ancho' : '',
  ].filter(Boolean);
  return modulos.join(' ');
}

/**
 * Aplica el estado visual a `body`/`documentElement` (atributos de datos,
 * tokens de radio de boton/tarjeta y clase de tipografia). Idempotente: se
 * puede llamar al montar el layout y luego al navegar.
 *
 * `doc` permite fijar el documento objetivo (por defecto el actual). El
 * preview del panel lo reutiliza para pintar el MISMO estado dentro de su
 * iframe (EDITOR = PREVIEW = STOREFRONT), sin duplicar el mapeo.
 *
 * @param {object} tienda
 * @param {Document} [doc] - documento donde aplicar el estado visual
 * @returns {object} estado visual normalizado
 */
export function aplicarEstadoVisual(tienda, doc = document) {
  const estado = estadoVisual(tienda);
  const body = doc.body;
  if (!body) return estado;

  body.dataset.cabecera = estado.header.id;
  body.dataset.tipografia = estado.tipografia;

  const raiz = doc.documentElement.style;
  raiz.setProperty('--sp-boton-radio', `${estado.botones.radio}px`);
  raiz.setProperty('--sp-tarjeta-radio', `${estado.tarjetas.radio}px`);
  raiz.setProperty('--sp-tarjeta-sombra', sombraTarjeta(estado.tarjetas.sombra));

  for (const clase of Object.keys(body.classList)) {
    if (clase.startsWith('sp-tipografia-')) body.classList.remove(clase);
  }
  body.classList.add(tipografiaClase(estado.tipografia));

  return estado;
}
